import { MediaService } from '@core/services/MediaService';
import type { ReactNode } from 'react';
import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

// Define the possible states for the audio player
export type AudioPlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

// Define the context interface
interface AudioPlayerContextType {
  // Current state
  activeAudioId: string | null;
  audioUrl: string | null;
  status: AudioPlayerStatus;
  error: string | null;

  // Audio controls
  currentTime: number;
  duration: number;
  volume: number;

  // Actions
  playAudio: (audioId: string, url: string, mediaServiceUrl?: string) => Promise<void>;
  pauseAudio: () => void;
  resumeAudio: () => void;
  stopAudio: () => void;
  setVolume: (volume: number) => void;
  seekTo: (time: number) => void;

  // UI state
  isExpanded: boolean;
  toggleExpand: () => void;
}

// Create the context with default values
const AudioPlayerContext = createContext<AudioPlayerContextType>({
  activeAudioId: null,
  audioUrl: null,
  status: 'idle',
  error: null,
  currentTime: 0,
  duration: 0,
  volume: 1,
  playAudio: async () => {},
  pauseAudio: () => {},
  resumeAudio: () => {},
  stopAudio: () => {},
  setVolume: () => {},
  seekTo: () => {},
  isExpanded: false,
  toggleExpand: () => {},
});

// Provider props interface
interface AudioPlayerProviderProps {
  children: ReactNode;
}

// Create the provider component
// eslint-disable-next-line max-statements
export function AudioPlayerProvider({ children }: AudioPlayerProviderProps) {
  // State for tracking which audio is active
  const [activeAudioId, setActiveAudioId] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<AudioPlayerStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // Audio control state
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);

  // UI state
  const [isExpanded, setIsExpanded] = useState(false);

  // Audio element ref and blob URL management
  const audioRef = useRef<HTMLAudioElement>(null);
  const [audioObjectUrl, setAudioObjectUrl] = useState<string | null>(null);

  // Cache MediaService instance to avoid creating new instances on every play
  const mediaServiceRef = useRef<{ baseUrl: string; service: MediaService } | null>(null);

  // Toggle expand/collapse
  const toggleExpand = useCallback(() => {
    setIsExpanded(prev => {
      // If we're collapsing the player and it's playing, pause it
      if (prev && status === 'playing') {
        setStatus('paused');
      }

      return !prev;
    });
  }, [status]);

  // Clean up blob URL when component unmounts or audio changes
  useEffect(
    () => () => {
      if (audioObjectUrl) {
        URL.revokeObjectURL(audioObjectUrl);
      }
    },
    [audioObjectUrl]
  );

  // Set up audio element event listeners
  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => {
      setStatus('playing');
    };

    const handlePause = () => {
      setStatus('paused');
    };

    const handleEnded = () => {
      setStatus('paused');
      setCurrentTime(0);
    };

    const handleError = () => {
      setStatus('error');
      setError('Failed to play audio');
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioObjectUrl]);

  // Play audio function - integrates with media service for secure fetching
  const playAudio = useCallback(
    // eslint-disable-next-line max-statements
    async (audioId: string, url: string, mediaServiceUrl?: string) => {
      try {
        // Set loading state
        setActiveAudioId(audioId);
        setAudioUrl(null);
        setStatus('loading');
        setError(null);
        setIsExpanded(true);

        // Clean up previous blob URL
        if (audioObjectUrl) {
          URL.revokeObjectURL(audioObjectUrl);
          setAudioObjectUrl(null);
        }

        let finalAudioUrl: string;

        if (mediaServiceUrl) {
          // Get or create MediaService instance (cached by baseUrl)
          let mediaService: MediaService;

          if (mediaServiceRef.current?.baseUrl === mediaServiceUrl) {
            // Reuse existing service if baseUrl matches
            mediaService = mediaServiceRef.current.service;
          } else {
            // Create new service and cache it
            mediaService = new MediaService({ baseUrl: mediaServiceUrl });
            mediaServiceRef.current = { baseUrl: mediaServiceUrl, service: mediaService };
          }

          // Use media service to fetch audio securely
          const audioBlob = await mediaService.getAudio(url);

          if (!audioBlob) {
            throw new Error('Failed to fetch audio file');
          }

          // Create blob URL
          const objectUrl = URL.createObjectURL(audioBlob);

          setAudioObjectUrl(objectUrl);
          finalAudioUrl = objectUrl;
        } else {
          // Use direct URL (fallback for cases without media service)
          finalAudioUrl = url;
        }

        // Set the audio URL and prepare for playback
        setAudioUrl(finalAudioUrl);

        // Set up audio element and start playback
        if (audioRef.current) {
          audioRef.current.src = finalAudioUrl;
          audioRef.current.volume = volume;
          await audioRef.current.play();
        }
      } catch (err) {
        // Handle error
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      }
    },
    [audioObjectUrl, volume]
  );

  // Pause audio
  const pauseAudio = useCallback(() => {
    if (audioRef.current && status === 'playing') {
      audioRef.current.pause();
    }
  }, [status]);

  // Resume audio
  const resumeAudio = useCallback(() => {
    if (audioRef.current && status === 'paused') {
      audioRef.current.play();
    }
  }, [status]);

  // Stop audio
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Clean up blob URL
    if (audioObjectUrl) {
      URL.revokeObjectURL(audioObjectUrl);
      setAudioObjectUrl(null);
    }

    setActiveAudioId(null);
    setAudioUrl(null);
    setStatus('idle');
    setCurrentTime(0);
    setDuration(0);
    setError(null);
    setIsExpanded(false);
  }, [audioObjectUrl]);

  // Set volume
  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  }, []);

  // Seek to specific time
  const seekTo = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }

    setCurrentTime(time);
  }, []);

  // Create the context value object
  const contextValue: AudioPlayerContextType = {
    activeAudioId,
    audioUrl,
    status,
    error,
    currentTime,
    duration,
    volume,
    playAudio,
    pauseAudio,
    resumeAudio,
    stopAudio,
    setVolume,
    seekTo,
    isExpanded,
    toggleExpand,
  };

  return (
    <AudioPlayerContext.Provider value={contextValue}>
      {children}
      {/* Hidden audio element for actual playback */}
      <audio ref={audioRef} style={{ display: 'none' }}>
        <track kind="captions" />
      </audio>
    </AudioPlayerContext.Provider>
  );
}

// Custom hook for using the audio player context
export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);

  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }

  return context;
}
